import { ContentList, Divider, SegmentedButton, SliderField, Text } from "@wayflyer/flyui";
import { fmtPercentage } from "shared-utils";
import { useIntl } from "react-intl";

import { type UseOfferReturn } from "../../hooks";
import { fmtAmountCommon, fmtFixedCollectionAmount } from "../../utils";
import { OfferProperty } from "../offer-property";

import { OFFER_BUILDER_MESSAGES } from "./defined-messages";

export type OfferBuilderProps = Omit<
  UseOfferReturn,
  | "id"
  | "deploymentDate"
  | "remittanceRateDailySalesPercent"
  | "remittanceRateInLowestDenomination"
  | "feeAmountInLowestDenomination"
  | "setCollectionType"
  | "formatter"
>;

const makeCurrencyPartsFormatter = (currency: string) => (value: number) => {
  const formatted = fmtAmountCommon(value * 100, currency); // e.g. "$12,800"
  const match = formatted.match(/^(\D+)?\s*([\d.,]+)\s*(\D+)?$/);

  return {
    left: match?.[1]?.trim() ?? undefined,
    value: match?.[2] ?? formatted,
    right: match?.[3]?.trim() ?? undefined,
    leftColor: "placeholder" as const,
    rightColor: "placeholder" as const,
  };
};

export const OfferBuilder = ({
  collectionFrequency,
  percentageOffers,
  fixedOffers,
  collectionType,
  deploymentAmountInLowestDenomination,
  remittanceRateIndex,
  deploymentAmountLowerBoundInLowestDenomination,
  deploymentAmountUpperBoundInLowestDenomination,
  deploymentCurrency,
  setDeploymentAmountInLowestDenomination,
  setRemittanceRateIndex,
}: OfferBuilderProps) => {
  const intl = useIntl();
  return (
    <>
      <Text color="disabled">{intl.formatMessage(OFFER_BUILDER_MESSAGES.sectionHeading).toUpperCase()}</Text>
      <SliderField
        label={intl.formatMessage(OFFER_BUILDER_MESSAGES.fundingProvidedTitle)}
        helperText={intl.formatMessage(OFFER_BUILDER_MESSAGES.fundingProvidedSubtext)}
        formatValueParts={makeCurrencyPartsFormatter(deploymentCurrency)}
        min={deploymentAmountLowerBoundInLowestDenomination / 100}
        max={deploymentAmountUpperBoundInLowestDenomination / 100}
        step={100}
        value={deploymentAmountInLowestDenomination / 100}
        onChange={(value: number) => setDeploymentAmountInLowestDenomination(value * 100)}
        onAfterChange={() => {}}
      />
      <Divider />
      <OfferProperty
        title={intl.formatMessage(OFFER_BUILDER_MESSAGES.remittanceAmountTitle)}
        subtext={
          collectionType === "percentage"
            ? intl.formatMessage(OFFER_BUILDER_MESSAGES.percentageRemittanceAmountSubtext)
            : intl.formatMessage(OFFER_BUILDER_MESSAGES.fixedRemittanceAmountSubtext)
        }
        value={
          <SegmentedButton
            key={`${deploymentAmountInLowestDenomination}-rate`}
            name="remittence_rate"
            defaultValue={remittanceRateIndex}
            onChange={(value) => {
              setRemittanceRateIndex(value as number);
            }}
            options={
              collectionType === "percentage"
                ? percentageOffers[deploymentAmountInLowestDenomination].map((offer, index) => {
                    return {
                      label: fmtPercentage(offer.remittanceRateDailySalesPercent),
                      value: index,
                    };
                  })
                : fixedOffers[deploymentAmountInLowestDenomination].map((offer, index) => {
                    return {
                      label: fmtFixedCollectionAmount(
                        offer.remittanceRateInLowestDenomination,
                        deploymentCurrency,
                        collectionFrequency
                      ),
                      value: index,
                    };
                  })
            }
          />
        }
        inverseFormat
      />
    </>
  );
};

export const OfferBuilderNext = ({
  collectionFrequency,
  percentageOffers,
  fixedOffers,
  collectionType,
  deploymentAmountInLowestDenomination,
  remittanceRateIndex,
  deploymentAmountLowerBoundInLowestDenomination,
  deploymentAmountUpperBoundInLowestDenomination,
  deploymentCurrency,
  setDeploymentAmountInLowestDenomination,
  setRemittanceRateIndex,
}: OfferBuilderProps) => {
  const intl = useIntl();
  return (
    <>
      <SliderField
        label={intl.formatMessage(OFFER_BUILDER_MESSAGES.fundingProvidedTitle)}
        helperText={intl.formatMessage(OFFER_BUILDER_MESSAGES.fundingProvidedSubtext)}
        formatValueParts={makeCurrencyPartsFormatter(deploymentCurrency)}
        min={deploymentAmountLowerBoundInLowestDenomination / 100}
        max={deploymentAmountUpperBoundInLowestDenomination / 100}
        step={100}
        value={deploymentAmountInLowestDenomination / 100}
        onChange={(value: number) => setDeploymentAmountInLowestDenomination(value * 100)}
        onAfterChange={() => {}}
      />
      <ContentList.Item
        title={intl.formatMessage(OFFER_BUILDER_MESSAGES.remittanceAmountTitle)}
        subtitle={
          collectionType === "percentage"
            ? intl.formatMessage(OFFER_BUILDER_MESSAGES.percentageRemittanceAmountSubtext)
            : intl.formatMessage(OFFER_BUILDER_MESSAGES.fixedRemittanceAmountSubtext)
        }
        summary={
          <SegmentedButton
            key={`${deploymentAmountInLowestDenomination}-rate`}
            name="remittence_rate"
            defaultValue={remittanceRateIndex}
            onChange={(value) => {
              setRemittanceRateIndex(value as number);
            }}
            options={
              collectionType === "percentage"
                ? percentageOffers[deploymentAmountInLowestDenomination].map((offer, index) => {
                    return {
                      label: fmtPercentage(offer.remittanceRateDailySalesPercent),
                      value: index,
                    };
                  })
                : fixedOffers[deploymentAmountInLowestDenomination].map((offer, index) => {
                    return {
                      label: fmtFixedCollectionAmount(
                        offer.remittanceRateInLowestDenomination,
                        deploymentCurrency,
                        collectionFrequency
                      ),
                      value: index,
                    };
                  })
            }
          />
        }
      />
    </>
  );
};
